import { useCallback, useMemo, useState } from 'react';

import type { InfiniteScrollProps } from './InfiniteScroll';
import useIsMounted from './useIsMounted';

const FETCH_MORE_RACE_CONDITION_REJECTION =
  'This fetchMore call was rejected because it happened while we were refetching the same query.';

interface Props {
  itemsCount: number;
  totalRecords: number;
  fetchMore?: (offset: number) => Promise<void>;
  loadMoreOffset?: number;
  isLoading: boolean;
}

export const useInfiniteScroll = ({
  itemsCount,
  totalRecords,
  fetchMore,
  loadMoreOffset = 100,
  isLoading: isLoadingProp,
}: Props): InfiniteScrollProps => {
  const isMounted = useIsMounted();
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const hasMore = itemsCount < totalRecords;
  const isLoading = isLoadingProp || isLoadingMore;
  const isLoadMoreAvailable = hasMore && !isLoading;

  const onLoadMore = useCallback(async () => {
    if (!isLoadMoreAvailable || !fetchMore) return;

    // Trying to fetchMore for an unmounted query will result in an error. We
    // don't have a way to cancel the cache update after completing the
    // request, so instead we catch the error and only rethrow it if the
    // component is still mounted (which suggests something else went wrong).
    // See: https://github.com/apollographql/apollo-client/issues/4114
    try {
      setIsLoadingMore(true);
      await fetchMore(itemsCount);
    } catch (error) {
      if (isMounted.current && error !== FETCH_MORE_RACE_CONDITION_REJECTION) {
        throw error;
      }
    } finally {
      setIsLoadingMore(false);
    }
  }, [isLoadMoreAvailable, fetchMore, itemsCount, isMounted]);

  return useMemo(
    () => ({
      hasMore,
      isLoadingMore: isLoading,
      onLoadMore,
      loadMoreOffset,
    }),
    [hasMore, isLoading, onLoadMore, loadMoreOffset],
  );
};
